/* ==========================================================================
   GSJ — shipping facts. ONE implementation of two questions:
     "what will checkout charge for shipping?" and
     "how far is this cart from free shipping?"

   Consumed by:
     assets/gs-panels.js         cart drawer + /cart page: bar, summary row,
                                 footer line, badge, reward ladder (non-CAD)
     assets/gsj-pdp-shipfact.js  the product page note (an EComposer code block)
                                 and the product trust strip (non-CAD)
     snippets/gsj-pdp-trust-strip.liquid  via the Liquid mirror below

   snippets/gsj-ship-fact.liquid mirrors this for server-side first paint. Keep
   the two in sync — the theme already uses that pattern for
   snippets/gs-cart-item.liquid <-> GSCart.lineHTML.

   Every threshold, rate and string comes from the "Shipping facts" theme
   settings, emitted once as JSON by layout/theme.liquid. Nothing is written
   here, so the four surfaces can never disagree.

   CURRENCY (Sept 24 2026). Every number in Shipping facts and in the reward
   ladder is CAD, but Shopify Markets prices the cart in the shopper's own
   currency. F.fx below is the ONE place those CAD figures are turned into the
   shopper's currency, using the rules checkout itself was measured to use on
   this store (the tests are written up in the handover):

     shipping rate price   CAD x rate, to the cent, then the market's
                           rounding rule (CAD 19.90 -> 14.35 -> US$15.00)
     free-shipping line    CAD x rate / (1 + conversion fee), to the cent.
                           Checkout converts rate CONDITIONS without Shopify
                           Payments' 2% conversion fee (CAD 450 -> US$318.07,
                           £240.78, €279.77; US$318 paid, US$319 free)
     reward amount         CAD x rate, to the cent (CAD 30 -> US$21.63)
     reward minimum        CAD x rate, to the cent (CAD 360 -> US$259.55)

   "rate" is Shopify.currency.rate, which already carries the conversion fee.
   Liquid has no equivalent, so the Liquid mirror only resolves CAD carts and
   leaves every other currency to this file. When a currency cannot be
   converted reliably (no rate, an unknown rounding rule, no fee setting) the
   facts return null and every surface says CALCULATED AT CHECKOUT instead of
   a number.
   ========================================================================== */
(function () {
  'use strict';

  var cfg = {};
  try {
    var el = document.getElementById('gsj-ship-config');
    if (el) cfg = JSON.parse(el.textContent || '{}') || {};
  } catch (e) { cfg = {}; }

  function num(v) { var n = parseFloat(v); return isFinite(n) ? n : 0; }
  function toCents(v) { return Math.round(num(v) * 100); }

  /* Compact money formatter driven by shop.money_format. Deliberately standalone
     (not GSCart's makeMoney) so this module has no load-order dependency on
     gs-panels.js — the product page uses it without the cart drawer's JS.
     shop.money_format follows the shopper's currency (£{{amount}},
     €{{amount_with_comma_separator}}, ¥{{amount_no_decimals}} ...). */
  function withCommas(cents, decimals, thousands, decimal) {
    var value = (cents / 100).toFixed(decimals == null ? 2 : decimals);
    var parts = value.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + (thousands || ','));
    return dollars + (parts[1] ? (decimal || '.') + parts[1] : '');
  }

  /* The market rounding rule Shopify applies to converted PRICES, product and
     shipping alike, as [step, ending] in cents: round UP to the next `step`,
     landing on `ending`. Measured on this store (CAD 295 / 55 / 95 pieces in
     each currency, and the shipping rate on the checkout page for USD, GBP and
     EUR). A currency that is not listed here is not guessed at: its shipping
     facts fall back to CALCULATED AT CHECKOUT. */
  var ROUNDING = {
    USD: [100, 0], GBP: [100, 0], EUR: [100, 95],
    AUD: [100, 0], NZD: [100, 0], CHF: [100, 0], SEK: [100, 0], DKK: [100, 0],
    PLN: [100, 0], CZK: [100, 0], SGD: [100, 0], HKD: [100, 0], CNY: [100, 0],
    TWD: [100, 0], ILS: [100, 0], AED: [100, 0],
    BBD: [100, 0], BSD: [100, 0], KYD: [100, 0], TTD: [100, 0], XCD: [100, 0],
    JPY: [10000, 0], INR: [10000, 0], HUF: [10000, 0], KRW: [100000, 0]
  };

  /* Currencies with no minor unit (ISO 4217). Cart amounts still arrive in
     hundredths, so a converted figure is rounded to a whole unit, as Shopify
     rounds it, not to a hundredth of a yen. */
  var WHOLE = {
    BIF: 1, CLP: 1, DJF: 1, GNF: 1, ISK: 1, JPY: 1, KMF: 1, KRW: 1, PYG: 1,
    RWF: 1, UGX: 1, VND: 1, VUV: 1, XAF: 1, XOF: 1, XPF: 1
  };

  /* ---- currency: the ONE place a CAD figure becomes the shopper's money ---- */
  var fx = (function () {
    var shop = String(cfg.shopCurrency || 'CAD').toUpperCase();
    var code = String(cfg.currency || shop).toUpperCase();
    var sc = window.Shopify && window.Shopify.currency;
    var active = sc && sc.active ? String(sc.active).toUpperCase() : null;
    var rate = sc && sc.rate != null ? parseFloat(sc.rate) : NaN;
    var fee = parseFloat(cfg.fxFee);
    var local = code === shop;

    /* The rate is trusted only when it belongs to the currency Liquid priced
       the cart in, so a page served in one currency can never be converted
       with another currency's rate. */
    var rateOk = local
      ? (active == null || active === shop)
      : (active === code && isFinite(rate) && rate > 0);
    var round = ROUNDING[code] || null;
    var shipOk = rateOk && (local || (!!round && isFinite(fee) && fee >= 0 && fee < 20));

    var unit = WHOLE[code] ? 100 : 1;
    function toUnit(v) { return Math.round(v / unit) * unit; }
    function roundUp(cents) {
      if (local || !round) return cents;
      var step = round[0], end = round[1];
      return Math.ceil((cents - end) / step) * step + end;
    }

    return {
      code: code,
      shop: shop,
      local: local,
      rate: local ? 1 : rate,
      fee: fee,
      /* Rewards only need the rate; shipping also needs the rounding rule and
         the fee. */
      rateOk: rateOk,
      shipOk: shipOk,
      /* A shipping rate, as checkout prints it. */
      price: function (cad) {
        if (local) return cad;
        return roundUp(toUnit(cad * rate));
      },
      /* A shipping rate CONDITION (the free-shipping minimum). */
      condition: function (cad) {
        if (local) return cad;
        return toUnit(cad * rate / (1 + fee / 100));
      },
      /* A fixed-amount discount, and its minimum-purchase requirement. */
      discount: function (cad) {
        if (local) return cad;
        return toUnit(cad * rate);
      },
      minimum: function (cad) {
        if (local) return cad;
        return toUnit(cad * rate);
      }
    };
  })();

  var F = {
    cfg: cfg,
    fx: fx,

    money: function (cents) {
      cents = Number(cents) || 0;
      var fmt = cfg.moneyFormat || '${{amount}}';
      var ph = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
      if (!ph) return '$' + withCommas(cents, 2);
      var val;
      switch (ph[1]) {
        case 'amount_no_decimals': val = withCommas(cents, 0); break;
        case 'amount_with_comma_separator': val = withCommas(cents, 2, '.', ','); break;
        case 'amount_with_space_separator': val = withCommas(cents, 2, ' ', ','); break;
        case 'amount_no_decimals_with_comma_separator': val = withCommas(cents, 0, '.', ','); break;
        case 'amount_no_decimals_with_space_separator': val = withCommas(cents, 0, ' ', ','); break;
        case 'amount_with_apostrophe_separator': val = withCommas(cents, 2, "'", '.'); break;
        default: val = withCommas(cents, 2);
      }
      return fmt.replace(/\{\{\s*\w+\s*\}\}/, val);
    },

    /* Round amounts read as "$250" rather than "$250.00" in short labels. */
    moneyClean: function (cents) { return this.money(cents).replace(/\.00\b/, ''); },

    /* Which of the delivery profile's three zones applies: Domestic (CA),
       US Cross-border, International. */
    market: function (country) {
      var c = String(country || cfg.country || '').toUpperCase();
      if (c === 'CA') return 'ca';
      if (c === 'US') return 'us';
      return 'intl';
    },

    /* Free-shipping minimum in the SHOPPER'S currency, as checkout tests it. */
    thresholdCents: function (country) {
      return fx.condition(toCents(this.market(country) === 'ca' ? cfg.freeCA : cfg.freeIntl));
    },

    /* Standard rate in the SHOPPER'S currency, as checkout prints it. */
    rateCents: function (country) {
      var m = this.market(country);
      return fx.price(toCents(m === 'ca' ? cfg.rateCA : m === 'us' ? cfg.rateUS : cfg.rateIntl));
    },

    /* The amount Shopify will actually test its free-shipping rate against.
       Shopify evaluates the rate's TOTAL_PRICE condition AFTER discounts, so
       `after_discounts` (cart.total_price) is the only basis that can never
       over-promise. `before_discounts` is honoured for Canada only: US and
       International advertise exactly the Shopify minimum, so they have no
       cushion for a discount code to eat into. The cart is already in the
       shopper's currency, so it is never converted. */
    basisCents: function (cart, country) {
      if (!cart) return 0;
      var after = cart.total_price || 0;
      if (cfg.basis !== 'before_discounts') return after;
      if (this.market(country) !== 'ca') return after;
      return cart.original_total_price == null ? after : cart.original_total_price;
    },

    /* What the cart reward ladder would take off an order whose ELIGIBLE
       subtotal is `eligibleCents` (shopper's currency). Mirrors the tier walk
       in sections/gs-cart-drawer.liquid; thresholds come from the shop
       metafield gsj.cart_reward_ladder (CAD) and are converted here the way
       checkout converts each code's minimum and amount. Codes are deliberately
       not emitted into this config — only the threshold/reward pairs the
       estimate needs. 0 when the rate is unknown, which only ever makes an
       estimate more conservative. */
    rewardCents: function (eligibleCents) {
      if (!fx.rateOk) return 0;
      var rungs = cfg.ladder || [];
      var cap = Number(cfg.ladderCap) || 0;
      var best = 0;
      for (var i = 0; i < rungs.length; i++) {
        var r = rungs[i].r;
        if (cap > 0 && r > cap) r = cap;
        if (eligibleCents >= fx.minimum(rungs[i].t) && r > best) best = r;
      }
      return best ? fx.discount(best) : 0;
    },

    /* The whole answer for one cart, in one object. null when this currency
       cannot be stated reliably: callers then say CALCULATED AT CHECKOUT and
       hide the bar rather than show a wrong number. */
    fact: function (basis, country) {
      if (!fx.shipOk) return null;
      var threshold = this.thresholdCents(country);
      var rate = this.rateCents(country);
      var unlocked = threshold > 0 && basis >= threshold;
      var remaining = Math.max(0, threshold - basis);
      var rowTpl = unlocked
        ? (cfg.rowFree || 'FREE')
        : (this.market(country) === 'ca' ? (cfg.rowCA || '[rate]') : (cfg.rowOther || '[rate]'));
      var barTpl = unlocked
        ? (cfg.barUnlocked || "YOU'VE UNLOCKED FREE SHIPPING")
        : (cfg.barChase || 'ADD [amount] MORE FOR FREE SHIPPING');
      return {
        threshold: threshold,
        rate: rate,
        basis: basis,
        unlocked: unlocked,
        remaining: remaining,
        /* 0 threshold means no free tier is advertised for this market, so the
           bar hides and the row falls back to the flat rate. */
        active: threshold > 0,
        shipValue: rowTpl.replace('[rate]', this.money(rate)),
        barText: barTpl.replace('[amount]', this.money(remaining)),
        percent: threshold > 0 ? Math.max(0, Math.min(100, (basis / threshold) * 100)) : 0
      };
    },

    /* Product page: the cart plus the piece being viewed, less the reward the
       ladder would auto-apply, so the note agrees with what checkout charges.
       `cartEligible` is the drawer's server-rendered eligible subtotal, which
       is present on every page. Every amount is in the shopper's currency. */
    pieceFact: function (opts) {
      var piece = Number(opts.pieceCents) || 0;
      var cart = Number(opts.cartCents) || 0;
      var gross = cart + piece;
      var basis = gross;
      if (cfg.basis !== 'before_discounts' || this.market(opts.country) !== 'ca') {
        var eligible = (Number(opts.cartEligibleCents) || 0) + (opts.pieceEligible ? piece : 0);
        basis = gross - this.rewardCents(eligible);
      }
      return this.fact(Math.max(0, basis), opts.country);
    }
  };

  window.GSJShipFact = F;
})();
